import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const AccountSettings = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDataExport = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/user/data-export', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `user-data-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        alert('Your data has been exported successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to export data');
      }
    } catch (error) {
      console.error('Failed to export data:', error);
      setError('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountDeletion = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/user/delete-account', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ confirmation: deleteConfirmation })
      });
      
      if (response.ok) {
        alert('Your account has been successfully deleted. You will be logged out.');
        logout();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
      setError('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
          
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
            Account Settings
          </h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Manage your account data and preferences
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Data Export Section */}
          <div style={{
            marginBottom: '32px',
            padding: '20px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <span style={{ fontSize: '24px' }}>📦</span>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                Export Your Data
              </h3>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: '1.4'
            }}>
              Download a complete copy of your account data including profile information, 
              reviews, photos, and preferences. This includes all data associated with your account.
            </p>
            
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '16px',
              padding: '8px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px'
            }}>
              <strong>What's included:</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                <li>Profile information and settings</li>
                <li>All reviews and ratings</li>
                <li>Uploaded photos and media</li>
                <li>Privacy and notification preferences</li>
                <li>Account activity and verification status</li>
              </ul>
            </div>
            
            <button
              onClick={handleDataExport}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Preparing Export...' : 'Export Data'}
            </button>
          </div>

          {/* Account Deletion Section */}
          <div style={{
            padding: '20px',
            backgroundColor: '#ffebee',
            borderRadius: '8px',
            border: '1px solid #ffcdd2'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#c62828' }}>
                Delete Account
              </h3>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: '1.4'
            }}>
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            
            <div style={{
              fontSize: '12px',
              color: '#c62828',
              marginBottom: '16px',
              padding: '8px',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px solid #ffcdd2'
            }}>
              <strong>This will permanently delete:</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                <li>Your profile and all personal information</li>
                <li>All reviews and ratings you've posted</li>
                <li>All uploaded photos and media</li>
                <li>Your account history and verification status</li>
                <li>All preferences and settings</li>
              </ul>
            </div>
            
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false);
              setDeleteConfirmation('');
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '400px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚨</div>
            
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', color: '#c62828' }}>
              Are you absolutely sure?
            </h3>
            
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '20px',
              lineHeight: '1.4'
            }}>
              This action cannot be undone. This will permanently delete your account 
              and remove all your data from our servers.
            </p>
            
            <div style={{ marginBottom: '20px', textAlign: 'left' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333'
              }}>
                Type "DELETE" to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
                placeholder="DELETE"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleAccountDeletion}
                disabled={loading || deleteConfirmation !== 'DELETE'}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (loading || deleteConfirmation !== 'DELETE') ? 'not-allowed' : 'pointer',
                  opacity: (loading || deleteConfirmation !== 'DELETE') ? 0.7 : 1
                }}
              >
                {loading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;